import gsap from 'gsap';
import { CatmullRomCurve3, type Object3D, Vector3 } from 'three';
import { config } from '../config';
import type { Ball, BallSystem } from '../machine/BallSystem';
import type { SelectionZone } from '../machine/SelectionZone';

interface SelectorAnim {
  /** Parent .position curve, sampled at t in [0,1]. */
  positionCurve: CatmullRomCurve3;
  /** Parent .scale.y as a function of t in [0,1]. */
  scaleY: (t: number) => number;
}

/**
 * Reveal sequence for a single ball within one machine.
 *
 * Flow:
 *   1. Ball is sucked up out of the chamber's top selector.
 *   2. Travels along the model's chrome arm — waypoints are auto-detected from TubeMesh* meshes
 *      in the GLB (sorted by trailing number). Falls back to a synthetic arch when the model
 *      has no such track.
 *   3. Lands at the end of the track (or at the procedural resultHolder fallback), scales up
 *      so the digit and colour are clearly readable, and idle-spins.
 *   4. Stays displayed until returnToPool() — called automatically at the start of the next draw.
 */
export class RevealAnimation {
  private idleSpins: Map<number, gsap.core.Tween> = new Map();
  /** Currently displayed ball id, if any. */
  private displayedBallId: number | null = null;

  constructor(
    private readonly balls: BallSystem,
    private readonly zone: SelectionZone,
    /** World-space resting target for the selected ball. If null, use zone.hoverPos. */
    private readonly resultHolderPos: Vector3 | null,
    /** Optional ordered world-space waypoints describing the visible chrome-arm track. */
    private readonly trackWaypoints: Vector3[],
    /**
     * Optional baked exit-trajectory keyframes from the GLB's `Ball1` (or equivalent) animation.
     * If present, the reveal animation follows this exact path — guaranteed to look correct
     * because it's what the 3D artist designed. Falls back to procedural arch otherwise.
     */
    private readonly exitPath: Vector3[],
    /** Node for the moving selector / cylinder (Ball_sellecter parent group). Null if absent. */
    private readonly selectorNode: Object3D | null,
    /** Local-space rest position the selector returns to after the rise-and-retract. */
    private readonly selectorRestPos: Vector3,
    /** Local-space rest scale of the selector node — restored after the reveal. */
    private readonly selectorRestScale: Vector3,
    /** Selector mesh geometry's local max.y — used to compute peak parent.y so the cylinder
     *  tip reaches the chamber's top exit cap when fully extended. */
    private readonly selectorMeshLocalTopY: number,
    /** World Y of the chamber's actual mesh top (where the ball exits). */
    private readonly chamberTopY: number,
  ) {}

  hasDisplayedBall(): boolean {
    return this.displayedBallId !== null;
  }

  getDisplayedBallId(): number | null {
    return this.displayedBallId;
  }

  async play(id: number): Promise<void> {
    const ball = this.balls.getBall(id);
    if (ball.state !== 'pool') {
      console.warn(`Reveal: ball ${id} not in pool (state=${ball.state}); aborting`);
      return;
    }

    // KEY: do NOT disable churn here. The other balls keep bouncing while the winner travels
    // out — same as a real lottery machine where the blower keeps running during the draw.
    ball.state = 'rising';
    ball.velocity.set(0, 0, 0);
    this.zone.setIntensity(1.1);

    const startPos = ball.position.clone();
    const target = this.computeTarget();
    const curve = this.buildForwardCurve(startPos, target);

    // The selector and the ball move IN PARALLEL during the reveal. The cylinder also inflates
    // its scale.y (the GLB compresses it 0.316×) so it visibly grows into a full-length push rod
    // that reaches the chamber's top exit cap at peak — matching what the model creator intended.
    const selectorAnim = this.buildSelectorAnim();

    await this.tweenAlongCurve(ball, curve, selectorAnim, config.reveal.transitDurationSec, 'power2.inOut');

    ball.state = 'displayed';
    this.displayedBallId = id;
    // No idle spin — once the ball settles in the result cup it stays still, like a real ball.
    this.scaleDisplayed(ball, config.reveal.displayedScale);

    this.zone.setIntensity(0.85);
  }

  /** Animate the currently-displayed ball back into the chamber pool. */
  async returnToPool(): Promise<void> {
    if (this.displayedBallId === null) return;
    const ball = this.balls.getBall(this.displayedBallId);
    if (ball.state !== 'displayed') {
      this.displayedBallId = null;
      return;
    }

    this.stopIdleSpin(ball.id);
    ball.state = 'returning';

    const chamberR = this.balls.chamberRadius;
    const chamberCenter = this.balls.chamberCenter;
    const startPos = ball.position.clone();
    const target = chamberCenter
      .clone()
      .add(new Vector3(
        (Math.random() - 0.5) * chamberR * 0.5,
        (Math.random() - 0.5) * chamberR * 0.5,
        (Math.random() - 0.5) * chamberR * 0.5,
      ));

    // Shrink back to normal size in parallel with travel.
    this.scaleDisplayed(ball, 1.0);

    const curve = this.buildReturnCurve(startPos, target);
    // No selector animation on return — the mechanism stays at rest while the ball arcs back.
    await this.tweenAlongCurve(ball, curve, null, config.reveal.returnDurationSec, 'power2.inOut');

    ball.state = 'pool';
    ball.velocity.set(0, 0, 0);
    this.displayedBallId = null;
  }

  forceResetDisplayedBall(): void {
    if (this.displayedBallId === null) return;
    const ball = this.balls.getBall(this.displayedBallId);
    this.stopIdleSpin(ball.id);
    ball.state = 'pool';
    ball.velocity.set(0, 0, 0);
    ball.position.copy(this.balls.chamberCenter);
    ball.mesh.position.copy(ball.position);
    ball.mesh.scale.setScalar(1);
    this.displayedBallId = null;
  }

  // ------------------ internals ------------------

  /** Where the ball should end up after the reveal. If the GLB has a baked exit path, its final
   *  keyframe is the model creator's designed result-display position. Otherwise fall back to the
   *  procedural tray. */
  private computeTarget(): Vector3 {
    if (this.exitPath.length > 0) {
      return this.exitPath[this.exitPath.length - 1]!.clone();
    }
    return (this.resultHolderPos ?? this.zone.hoverPos).clone();
  }

  /** Useful subset of the auto-detected track — drops any waypoints that are clearly still
   *  inside the chamber (closer to chamber center than chamberRadius * 1.05). Sorted by their
   *  X distance from chamber center, so the path runs in the natural inside-to-outside order. */
  private usefulTrackPoints(): Vector3[] {
    if (this.trackWaypoints.length === 0) return [];
    const center = this.balls.chamberCenter;
    const r = this.balls.chamberRadius;
    const outside = this.trackWaypoints.filter((wp) => wp.distanceTo(center) >= r * 1.05);
    outside.sort((a, b) => Math.abs(a.x - center.x) - Math.abs(b.x - center.x));
    return outside;
  }

  /** Build the chamber → result curve. Prefers the baked exit path from the GLB. */
  private buildForwardCurve(startPos: Vector3, target: Vector3): CatmullRomCurve3 {
    // 1. Baked path from the artist — highest fidelity.
    if (this.exitPath.length > 0) {
      // Prepend our ball's current position so the curve lerps from wherever it was floating
      // to the start of the artist's designed path, then runs along it to the result dock.
      const points: Vector3[] = [startPos, ...this.exitPath.map((p) => p.clone())];
      return new CatmullRomCurve3(points, false, 'catmullrom', 0.5);
    }

    // 2. Procedural fallback: force the ball up through the selector cap, over the chamber,
    //    down into a "tray" position.
    const chamberR = this.balls.chamberRadius;
    const chamberCenter = this.balls.chamberCenter;
    const chamberTopY = chamberCenter.y + chamberR;
    const selectorExit = new Vector3(
      chamberCenter.x,
      chamberTopY + chamberR * 0.35,
      chamberCenter.z,
    );

    const points: Vector3[] = [startPos, selectorExit];
    const trackOutside = this.usefulTrackPoints();
    if (trackOutside.length > 0) {
      for (const wp of trackOutside) points.push(wp.clone());
    } else {
      points.push(
        new Vector3(
          (selectorExit.x + target.x) / 2,
          chamberTopY + chamberR * 0.5,
          (selectorExit.z + target.z) / 2,
        ),
      );
    }
    points.push(new Vector3(target.x, target.y + chamberR * 0.7, target.z));
    points.push(target);

    return new CatmullRomCurve3(points, false, 'catmullrom', 0.5);
  }

  /** Build the result → chamber return curve. Walks the baked exit path in reverse if present. */
  private buildReturnCurve(startPos: Vector3, target: Vector3): CatmullRomCurve3 {
    if (this.exitPath.length > 0) {
      // Reverse the artist's path: dock → ... → chamber. End with our target inside the chamber.
      const reversed = this.exitPath.slice().reverse();
      const points: Vector3[] = [startPos, ...reversed.slice(1).map((p) => p.clone()), target];
      return new CatmullRomCurve3(points, false, 'catmullrom', 0.5);
    }

    const chamberR = this.balls.chamberRadius;
    const chamberCenter = this.balls.chamberCenter;
    const chamberTopY = chamberCenter.y + chamberR;

    const points: Vector3[] = [startPos];

    const trackOutside = this.usefulTrackPoints();
    if (trackOutside.length > 0) {
      for (let i = trackOutside.length - 1; i >= 0; i--) {
        points.push(trackOutside[i]!.clone());
      }
    }

    points.push(
      new Vector3(chamberCenter.x, chamberTopY + chamberR * 0.3, chamberCenter.z),
      target,
    );

    return new CatmullRomCurve3(points, false, 'catmullrom', 0.5);
  }

  private tweenAlongCurve(
    ball: Ball,
    curve: CatmullRomCurve3,
    selectorAnim: SelectorAnim | null,
    duration: number,
    ease: string,
  ): Promise<void> {
    const ballSample = new Vector3();
    const selSample = new Vector3();
    const node = this.selectorNode;
    return new Promise((resolve) => {
      const proxy = { t: 0 };
      gsap.to(proxy, {
        t: 1,
        duration,
        ease,
        onUpdate: () => {
          curve.getPoint(proxy.t, ballSample);
          ball.position.copy(ballSample);
          ball.mesh.position.copy(ballSample);
          // Tumble while traveling — looks like the ball is rolling through the tube.
          ball.mesh.rotation.x += 0.18;
          ball.mesh.rotation.z -= 0.12;
          if (selectorAnim && node) {
            selectorAnim.positionCurve.getPoint(proxy.t, selSample);
            node.position.copy(selSample);
            node.scale.y = selectorAnim.scaleY(proxy.t);
          }
        },
        onComplete: () => {
          curve.getPoint(1, ballSample);
          ball.position.copy(ballSample);
          ball.mesh.position.copy(ballSample);
          if (node) {
            node.position.copy(this.selectorRestPos);
            node.scale.copy(this.selectorRestScale);
          }
          resolve();
        },
      });
    });
  }

  private scaleDisplayed(ball: Ball, scale: number): void {
    gsap.to(ball.mesh.scale, {
      x: scale,
      y: scale,
      z: scale,
      duration: config.reveal.displayedScaleTweenSec,
      ease: 'back.out(1.4)',
    });
  }

  private stopIdleSpin(id: number): void {
    const tw = this.idleSpins.get(id);
    if (tw) {
      tw.kill();
      this.idleSpins.delete(id);
    }
  }

  /**
   * Procedural rise-and-retract animation for the selector cylinder.
   *
   * The model's Ball_sellecter has a parent scale.y of ~0.316 which visually compresses the
   * cylinder from its natural 14.3-unit local length down to ~4.5 world units. The reveal
   * inflates that scale back toward 1.0 while raising the parent's position, so the cylinder
   * visibly EXTENDS UPWARD into the sphere and reaches the top exit cap at peak — looking
   * like a full-length push rod delivering the ball.
   *
   * We do NOT use the GLB's baked Ball_sellecter track: that track moves the parent DOWN,
   * which made the (already short) cylinder poke out below the wooden base.
   *
   * At peak with scale.y=1.0 the cylinder's world-space TOP is parent.y + 1 * meshLocalTopY.
   * Set parent.y so that top lines up with the chamber's exit cap (slight overshoot for visual
   * clarity). The cylinder's BOTTOM at peak sits at parent.y + meshLocalBottomY — which works
   * out to roughly the chamber bottom for the placeholder model, giving a full-chamber span.
   */
  private buildSelectorAnim(): SelectorAnim | null {
    if (!this.selectorNode) return null;
    const rest = this.selectorRestPos;
    const restScaleY = this.selectorRestScale.y;
    const peakScaleY = 1.0;
    const topOvershoot = 0.5;
    const peakY = this.chamberTopY - peakScaleY * this.selectorMeshLocalTopY + topOvershoot;

    const N = 32;
    const points: Vector3[] = [];
    const scaleSamples: number[] = [];
    for (let i = 0; i < N; i++) {
      const t = i / (N - 1);
      // Bias peak slightly earlier (t≈0.42) so the rod reaches the exit hole right when the ball
      // is being pushed out the top, then retracts as the ball arcs along the chrome arm.
      const warped = Math.pow(t, 1.2);
      const sin = Math.sin(warped * Math.PI);
      points.push(new Vector3(rest.x, rest.y + sin * (peakY - rest.y), rest.z));
      scaleSamples.push(restScaleY + sin * (peakScaleY - restScaleY));
    }
    const positionCurve = new CatmullRomCurve3(points, false, 'catmullrom', 0.5);
    const scaleY = (t: number): number => {
      const u = Math.max(0, Math.min(1, t));
      const idxF = u * (N - 1);
      const i0 = Math.floor(idxF);
      const i1 = Math.min(N - 1, i0 + 1);
      const f = idxF - i0;
      return scaleSamples[i0]! * (1 - f) + scaleSamples[i1]! * f;
    };
    return { positionCurve, scaleY };
  }
}
